import { useEffect, useState } from 'react'
import {
    MdChevronLeft,
    MdChevronRight,
    MdFirstPage,
    MdFullscreen,
    MdFullscreenExit,
    MdLastPage,
} from 'react-icons/md'
import { ScrollMinimap, usePannableScroll } from '@/components/pannable-scroll'
import { DetailType, useDetailPopup } from '@/components/detail-popup'
import {
    BILLING_STATUS_COLORS,
    BILLING_STATUS_IN_PROGRESS,
    BILLING_STATUS_IN_PROGRESS_LABEL,
    BILLING_STATUS_LABELS,
    BILLING_STATUS_NONE_LABEL,
    formatCurrency,
    ORDER_TYPE_LABELS,
    PAYMENT_STATUS_LABELS,
    ReportConfig,
} from '../config/report-configs'

interface PaginationProps {
    page: number
    pageSize: number
    pageCount: number
    onPageChange: (page: number) => void
    onPageSizeChange: (size: number) => void
}

interface ReportTableProps {
    data: any[]
    config: ReportConfig
    loading: boolean
    total: number
    reportType: string
    pagination?: PaginationProps
}

const PAGE_SIZES = [10, 20, 50, 100]

/**
 * Tipo de relatório -> página de detalhe aberta no modal.
 *
 * `marketing-funnel` lista Leads (cada linha é um lead com seus negócios), então
 * abre o detalhe de lead. Internações e Atendimentos não têm página de detalhe —
 * ficam de fora e a linha não é clicável.
 */
const DETAIL_TYPE_BY_REPORT: Record<string, DetailType> = {
    consultations: 'consultation',
    exams: 'exam',
    orders: 'order',
    billings: 'billing',
    clients: 'client',
    deals: 'deal',
    leads: 'lead',
    'marketing-funnel': 'lead',
}

export default function ReportTableComponent({
    data,
    config,
    loading,
    total,
    reportType,
    pagination,
}: ReportTableProps) {
    const [isFullscreen, setIsFullscreen] = useState(false)
    const { openDetail } = useDetailPopup()

    // Recalcula o viewport quando muda o que altera a largura/altura do conteúdo.
    const {
        ref: scrollRef,
        viewport,
        hasOverflow,
        didPan,
        panHandlers,
    } = usePannableScroll<HTMLDivElement>(
        `${reportType}:${config.columns.length}:${data.length}:${isFullscreen}`
    )

    const detailType = DETAIL_TYPE_BY_REPORT[reportType]

    const handleRowClick = (row: any) => {
        // O `click` dispara após o `pointerup`: sem isso, todo arrasto da tabela
        // terminaria abrindo o detalhe da linha sob o cursor.
        if (didPan()) return
        if (!detailType || row.id == null) return
        // Não sequestra o clique quando o usuário está selecionando texto da célula.
        if (window.getSelection()?.toString()) return
        openDetail(detailType, row.id)
    }

    useEffect(() => {
        if (!isFullscreen) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsFullscreen(false)
        }
        window.addEventListener('keydown', onKeyDown)
        // Trava o scroll da página atrás do overlay.
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = previousOverflow
        }
    }, [isFullscreen])

    const minimapSegments = config.columns.map(col => ({
        key: col.key,
        label: col.label,
    }))

    const renderCellValue = (col: (typeof config.columns)[0], row: any) => {
        const value = row[col.key]

        if (col.key === 'status') {
            const statusLabel = config.statusLabels[value] || value
            const statusColor = config.statusColors[value] || 'badge-ghost'
            return (
                <span className={`badge badge-sm ${statusColor}`}>
                    {statusLabel}
                </span>
            )
        }

        // Status da cobrança vinculada (relação to-one `billing`).
        if (col.key === 'billingStatus') {
            // No pagamento em partes (flex, parcelas avulsas) o backend manda o status
            // do CONJUNTO: a âncora é só um dos pagamentos, e sozinha mostrava
            // "Cancelado" num procedimento cuja venda foi inteiramente paga pelas
            // outras cobranças. Pagamento simples não traz o derivado.
            const billingStatus =
                row.billingPaymentState?.effectiveStatus ?? row.billing?.status
            if (!billingStatus) {
                return (
                    <span className="badge badge-sm badge-outline text-base-content/50">
                        {BILLING_STATUS_NONE_LABEL}
                    </span>
                )
            }
            const label =
                billingStatus === BILLING_STATUS_IN_PROGRESS
                    ? BILLING_STATUS_IN_PROGRESS_LABEL
                    : BILLING_STATUS_LABELS[billingStatus] || billingStatus
            const color =
                billingStatus === BILLING_STATUS_IN_PROGRESS
                    ? 'badge-accent'
                    : BILLING_STATUS_COLORS[billingStatus] || 'badge-ghost'
            const paid = row.billingPaymentState
            return (
                <span
                    className={`badge badge-sm ${color}`}
                    // Em partes, o número responde "quanto falta?" sem abrir a linha.
                    title={
                        paid
                            ? `Pago ${formatCurrency(paid.paidTotal)} de ${formatCurrency(paid.dueTotal)}`
                            : undefined
                    }
                >
                    {label}
                </span>
            )
        }

        if (col.key === 'type' && reportType === 'billings') {
            const isPayable = value === 'PAYABLE'
            return (
                <span
                    className={`badge badge-sm ${isPayable ? 'badge-error' : 'badge-success'}`}
                >
                    {isPayable ? 'A Pagar' : 'A Receber'}
                </span>
            )
        }

        if (col.key === 'paymentStatus' && reportType === 'orders') {
            const label = PAYMENT_STATUS_LABELS[value] || value || '-'
            return <span>{label}</span>
        }

        if (col.key === 'type' && reportType === 'orders') {
            return <span>{ORDER_TYPE_LABELS[value] || value || '-'}</span>
        }

        if (col.render) {
            return <span>{col.render(value, row)}</span>
        }

        return <span>{value ?? '-'}</span>
    }

    const alignClass = (col: (typeof config.columns)[0]) => {
        if (col.align === 'right') return 'text-right'
        if (col.align === 'center') return 'text-center'
        return 'text-left'
    }

    if (loading) {
        return (
            <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                <div className="skeleton h-4 w-48 mb-4" />
                <div className="overflow-x-auto">
                    <table className="table">
                        <thead>
                            <tr>
                                {config.columns.map(col => (
                                    <th
                                        key={col.key}
                                        className={alignClass(col)}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[...Array(6)].map((_, i) => (
                                <tr key={i}>
                                    {config.columns.map(col => (
                                        <td key={col.key}>
                                            <div className="skeleton h-4 w-20" />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    const showingFrom = pagination
        ? (pagination.page - 1) * pagination.pageSize + 1
        : 1
    const showingTo = pagination
        ? Math.min(pagination.page * pagination.pageSize, total)
        : data.length

    return (
        <div
            className={
                isFullscreen
                    ? 'fixed inset-0 z-[999] bg-base-100 flex flex-col'
                    : 'bg-base-100 rounded-xl border border-base-300/50 overflow-hidden flex flex-col'
            }
        >
            <div className="flex-shrink-0 px-5 py-3 border-b border-base-300/50 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-base-content/70">
                    {data.length === 0
                        ? 'Nenhum registro encontrado'
                        : pagination
                          ? `Exibindo ${showingFrom}-${showingTo} de ${total}`
                          : `${data.length} ${data.length === 1 ? 'registro' : 'registros'}`}
                </p>
                <div className="flex items-center gap-3 text-sm">
                    {pagination && (
                        <div className="flex items-center gap-2">
                            <select
                                className="select select-bordered select-sm"
                                value={pagination.pageSize}
                                onChange={e =>
                                    pagination.onPageSizeChange(
                                        Number(e.target.value)
                                    )
                                }
                            >
                                {PAGE_SIZES.map(s => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost gap-1"
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        title={
                            isFullscreen
                                ? 'Sair do modo expandido (Esc)'
                                : 'Modo expandido'
                        }
                    >
                        {isFullscreen ? (
                            <MdFullscreenExit className="text-lg" />
                        ) : (
                            <MdFullscreen className="text-lg" />
                        )}
                        <span className="hidden sm:inline text-xs">
                            {isFullscreen ? 'Sair' : 'Expandir'}
                        </span>
                    </button>
                </div>
            </div>

            {/* Minimapa: só quando a tabela realmente transborda na horizontal. */}
            {data.length > 0 && hasOverflow && (
                <div className="flex-shrink-0 px-5 py-2 border-b border-base-300/30 bg-base-200/30">
                    <ScrollMinimap
                        targetRef={scrollRef}
                        viewport={viewport}
                        segments={minimapSegments}
                    />
                </div>
            )}

            {data.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-base-content/40">
                    <span className="text-sm">
                        Nenhum dado encontrado no período selecionado.
                    </span>
                </div>
            ) : (
                <div
                    ref={scrollRef}
                    {...panHandlers}
                    className={`overflow-auto ${isFullscreen ? 'flex-1' : ''} ${
                        // Sem `select-none`: mataria copiar valores das células. O
                        // `preventDefault()` do pointerdown já impede que o pan vire
                        // seleção de texto.
                        hasOverflow ? 'cursor-grab' : ''
                    }`}
                >
                    <table className="table table-zebra table-sm">
                        {/* Fundo opaco: translúcido deixaria as linhas passarem por baixo. */}
                        <thead className="bg-base-200 sticky top-0 z-10">
                            <tr>
                                {config.columns.map(col => (
                                    <th
                                        key={col.key}
                                        className={`${alignClass(col)} text-xs uppercase tracking-wide text-base-content/60`}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => {
                                const clickable = Boolean(
                                    detailType && row.id != null
                                )
                                return (
                                    <tr
                                        key={row.id || row.partnerId || index}
                                        className={`hover:bg-base-200/40 ${clickable ? 'cursor-pointer' : ''}`}
                                        onClick={
                                            clickable
                                                ? () => handleRowClick(row)
                                                : undefined
                                        }
                                        title={
                                            clickable
                                                ? 'Ver detalhes'
                                                : undefined
                                        }
                                        // Focável para acesso por teclado, mas SEM `role="button"`:
                                        // ele destruiria a semântica da tabela e, pior, casa com o
                                        // seletor INTERACTIVE do usePannableScroll — o que desligaria
                                        // o drag-to-pan em todas as linhas.
                                        tabIndex={clickable ? 0 : undefined}
                                        onKeyDown={
                                            clickable
                                                ? e => {
                                                      if (
                                                          e.key === 'Enter' ||
                                                          e.key === ' '
                                                      ) {
                                                          e.preventDefault()
                                                          openDetail(
                                                              detailType,
                                                              row.id
                                                          )
                                                      }
                                                  }
                                                : undefined
                                        }
                                    >
                                        {config.columns.map(col => (
                                            <td
                                                key={col.key}
                                                className={alignClass(col)}
                                            >
                                                {renderCellValue(col, row)}
                                            </td>
                                        ))}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {pagination && pagination.pageCount > 1 && (
                <div className="flex-shrink-0 px-5 py-3 border-t border-base-300/50 flex items-center justify-center gap-1">
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={pagination.page === 1}
                        onClick={() => pagination.onPageChange(1)}
                        aria-label="Primeira página"
                    >
                        <MdFirstPage className="text-lg" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={pagination.page === 1}
                        onClick={() =>
                            pagination.onPageChange(pagination.page - 1)
                        }
                        aria-label="Página anterior"
                    >
                        <MdChevronLeft className="text-lg" />
                    </button>
                    <span className="text-sm px-3 text-base-content/70">
                        Página <strong>{pagination.page}</strong> de{' '}
                        <strong>{pagination.pageCount}</strong>
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={pagination.page === pagination.pageCount}
                        onClick={() =>
                            pagination.onPageChange(pagination.page + 1)
                        }
                        aria-label="Próxima página"
                    >
                        <MdChevronRight className="text-lg" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={pagination.page === pagination.pageCount}
                        onClick={() =>
                            pagination.onPageChange(pagination.pageCount)
                        }
                        aria-label="Última página"
                    >
                        <MdLastPage className="text-lg" />
                    </button>
                </div>
            )}
        </div>
    )
}
